import { DateRangeFilter } from "@/components/date-filter/DateRangeFilter.tsx";
import { Search } from "@/components/search/Search.tsx";
import {
  parseUrlHelper,
  Box,
  Button,
  Input,
  Modal,
  ModalProps,
  Multiselect,
  SelectItem,
  Stack,
  Switch,
  Title,
  useModals,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useEmployeeSearchInline, {
  useEmployeeMe,
  useEmployeesByIds,
} from "@/queries/employees/use-employees.query.ts";
import { useDisqualifications } from "@/queries/recruit/use-disqualifications.query.ts";
import {
  useJobDetails,
  useJobRecruitersSearch,
  useJobsSearch,
  useJobStakeholdersSearch,
} from "@/queries/recruit/use-jobs.query.ts";
import { useStages } from "@/queries/recruit/use-stages.query.ts";
import { useLabels } from "@/queries/use-labels.query.ts";
import { useSources } from "@/queries/use-sources.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ApiApplicantFilter } from "@/types/recruit/applicant.types.ts";
import { ApiJobsFilter, ApiJobsFilterDefault } from "@/types/recruit/job.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { unionBy, uniq } from "lodash";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

export interface ApplicantsFilterModalProps extends ModalProps {
  filter: ApiApplicantFilter;
}

export function ApplicantsFilterModal({ filter, ...props }: ApplicantsFilterModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { canIView } = usePermissions();
  const navigate = useNavigate();
  const canViewRecruitingData = canIView(ApiPermission.recruiting_module);
  const canSearchEmployees =
    canIView(ApiPermission.employees_search) || canIView(ApiPermission.see_employee_directory);

  const { data: employeeMe } = useEmployeeMe();

  const { data: sources } = useSources(canViewRecruitingData);
  const { data: disqualificationReasons } = useDisqualifications(canViewRecruitingData);
  const { data: labels, isLoading: labelsIsLoading } = useLabels(canViewRecruitingData);
  const { data: recruitersData, isLoading: recruitersIsLoading } =
    useJobRecruitersSearch(canViewRecruitingData);

  const recruiters =
    recruitersData?.items?.map((recruiter) => ({
      id: recruiter.id,
      title: renderEmployeeName(recruiter),
    })) || [];

  const { data: stakeholdersData, isLoading: stakeholdersIsLoading } =
    useJobStakeholdersSearch(canViewRecruitingData);

  const stakeholders =
    stakeholdersData?.items?.map((stakeholder) => ({
      id: stakeholder.id,
      title: renderEmployeeName(stakeholder),
    })) || [];

  const actionEmployeeSearch = useEmployeeSearchInline(canSearchEmployees);
  const authorSearch = useEmployeeSearchInline(canSearchEmployees);

  const [jobsFilter, setJobsFilter] = useState<ApiJobsFilter>(ApiJobsFilterDefault);
  const { data: jobsSearched, isLoading: jobsIsLoading } = useJobsSearch(
    jobsFilter,
    canViewRecruitingData
  );

  const ApplicantsFilterSchemaObject = z.object({
    text: z.string().optional(),
    location: z.string().optional(),
    company: z.string().optional(),
    job_ids: z.array(z.number()),
    stage_ids: z.array(z.number()),
    source_ids: z.array(z.number()),
    disqualification_reason_ids: z.array(z.number()),
    position: z.string().optional(),
    date_last_candidate_action_from: z.date().nullable().optional(),
    date_last_candidate_action_to: z.date().nullable().optional(),
    label_ids: z.array(z.number()),
    comment: z.string().optional(),
    date_applicant_birth_from: z.date().nullable().optional(),
    date_applicant_birth_to: z.date().nullable().optional(),
    recruiter_ids: z.array(z.number()),
    stakeholder_ids: z.array(z.number()),
    action_employee_ids: z.array(z.number()),
    author_ids: z.array(z.number()),
    assigned_by_ids: z.array(z.number()),
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(ApplicantsFilterSchemaObject),
    defaultValues: {
      text: filter.text || "",
      job_ids: filter.job_ids || [],
      stage_ids: filter.pipeline_stage_ids || [],
      location: filter.location || "",
      company: filter.company || "",
      source_ids: filter.source_ids || [],
      disqualification_reason_ids: filter.disqualification_reason_ids || [],
      position: filter.position || "",
      date_last_candidate_action_from: filter.date_last_candidate_action_from
        ? new Date(filter.date_last_candidate_action_from)
        : undefined,
      date_last_candidate_action_to: filter.date_last_candidate_action_to
        ? new Date(filter.date_last_candidate_action_to)
        : undefined,
      label_ids: filter.label_ids || [],
      comment: filter.comment || "",
      date_applicant_birth_from: filter.date_applicant_birth_from
        ? new Date(filter.date_applicant_birth_from)
        : undefined,
      date_applicant_birth_to: filter.date_applicant_birth_to
        ? new Date(filter.date_applicant_birth_to)
        : undefined,
      recruiter_ids: filter.recruiter_ids || [],
      stakeholder_ids: filter.stakeholder_ids || [],
      action_employee_ids: filter.action_employee_ids || [],
      author_ids: filter.author_ids || [],
      assigned_by_ids: filter.assigned_by_ids || [],
    },
  });

  const { data: employeesInOptions } = useEmployeesByIds(
    uniq((filter.action_employee_ids ?? []).concat(filter.author_ids ?? [])),
    canSearchEmployees
  );

  const formOptions = useMemo(() => {
    const actionEmployees: SelectItem[] =
      employeesInOptions
        ?.filter((employee) => filter.action_employee_ids?.includes(employee.id))
        .map((employee) => ({
          id: employee.id,
          title: renderEmployeeName(employee),
        })) ?? [];

    const authorEmployees: SelectItem[] =
      employeesInOptions
        ?.filter((employee) => filter.author_ids?.includes(employee.id))
        .map((employee) => ({
          id: employee.id,
          title: renderEmployeeName(employee),
        })) ?? [];

    return {
      actionEmployeeSearch: unionBy(actionEmployees, actionEmployeeSearch.options, "id"),
      authorSearch: unionBy(authorEmployees, authorSearch.options, "id"),
    };
  }, [
    employeesInOptions,
    actionEmployeeSearch,
    authorSearch,
    filter.action_employee_ids,
    filter.author_ids,
    renderEmployeeName,
  ]);

  const onSubmit = handleSubmit((filterSchema) => {
    const newParams: URLSearchParams = new URLSearchParams();

    if (filterSchema.text) newParams.append("text", filterSchema.text);
    if (filterSchema.job_ids.length > 0) newParams.append("job_id", filterSchema.job_ids.join(","));
    if (filterSchema.location) newParams.append("location", filterSchema.location);
    if (filterSchema.company) newParams.append("company", filterSchema.company);
    if (filterSchema.stage_ids.length > 0)
      newParams.append("stage_id", filterSchema.stage_ids.join(","));
    if (filterSchema.source_ids.length > 0)
      newParams.append("source_id", filterSchema.source_ids.join(","));
    if (filterSchema.disqualification_reason_ids.length > 0)
      newParams.append(
        "disqualification_reason_id",
        filterSchema.disqualification_reason_ids.join(",")
      );
    if (filterSchema.position) newParams.append("position", filterSchema.position);
    if (filterSchema.date_last_candidate_action_from)
      newParams.append(
        "last_candidate_action_from",
        parseUrlHelper.formatDate(filterSchema.date_last_candidate_action_from)
      );
    if (filterSchema.date_last_candidate_action_to)
      newParams.append(
        "last_candidate_action_to",
        parseUrlHelper.formatDate(filterSchema.date_last_candidate_action_to)
      );
    if (filterSchema.label_ids.length > 0)
      newParams.append("label_id", filterSchema.label_ids.join(","));
    if (filterSchema.comment) newParams.append("comment", filterSchema.comment);
    if (filterSchema.date_applicant_birth_from)
      newParams.append(
        "date_applicant_birth_from",
        parseUrlHelper.formatDate(filterSchema.date_applicant_birth_from)
      );
    if (filterSchema.date_applicant_birth_to)
      newParams.append(
        "date_applicant_birth_to",
        parseUrlHelper.formatDate(filterSchema.date_applicant_birth_to)
      );
    if (filterSchema.recruiter_ids.length > 0)
      newParams.append("recruiter_id", filterSchema.recruiter_ids.join(","));
    if (filterSchema.stakeholder_ids.length > 0)
      newParams.append("stakeholder_id", filterSchema.stakeholder_ids.join(","));
    if (filterSchema.action_employee_ids.length > 0)
      newParams.append("action_employee_id", filterSchema.action_employee_ids.join(","));
    if (filterSchema.author_ids.length > 0)
      newParams.append("author_id", filterSchema.author_ids.join(","));
    if (filterSchema.assigned_by_ids.length > 0)
      newParams.append("assigned_by_ids", filterSchema.assigned_by_ids.join(","));

    analyticsService.trackEvent(analyticEvents.applicants.filtered);

    if (newParams) {
      navigate(`/recruitment/candidates?${newParams.toString()}`);
    } else {
      navigate("/recruitment/candidates");
    }

    close(id);
  });

  function handleClearFilters() {
    const newParams: URLSearchParams = new URLSearchParams();

    if (filter.job_ids && filter.job_ids.length == 1) {
      newParams.append("job_id", filter.job_ids.join(","));
    }

    navigate(`/recruitment/candidates?${newParams.toString()}`);

    close(id);
  }

  const [searchParams] = useSearchParams();
  const jobIds = parseUrlHelper.getNumberArray(searchParams, "job_id");
  const { data: stages, isLoading: isLoadingStages } = useStages(canViewRecruitingData);

  async function handleSearchJobs(text: string | undefined) {
    setJobsFilter({
      ...jobsFilter,
      text: text,
    });
  }

  const { data: currentJob } = useJobDetails(jobIds && jobIds.length == 1 ? jobIds[0] : undefined);

  const canShowJobAndStageFilters =
    (jobIds?.length ?? 0) !== 1 && (filter.job_ids?.length ?? 0) !== 1;

  return (
    <>
      <Modal
        {...props}
        variant={"side"}
        title={t("ats:filter_candidates")}
        layout={"stretch"}
        withCloser
        closeByEscEnabled
        closeByOutsidePress
        actions={
          <Stack gap={"sm"}>
            <Button size={"lg"} className={"w-full"} type={"submit"} onClick={onSubmit}>
              {t("common:show_results")}
            </Button>
            <Button
              size={"md"}
              variant={"plain"}
              onClick={() => {
                handleClearFilters();
              }}
            >
              {t("common:filter_clear")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Title header={t("common:search")} size={"xs"} paddingBottom={true} />
          <Controller
            render={({ field: { value, onChange } }) => (
              <Search
                className={"w-full"}
                placeholder={t("ats:search_placeholder_name_phone_links_email") + "..."}
                value={value}
                onInput={onChange}
              />
            )}
            control={control}
            name={"text"}
          />
          <Title header={t("common:filter")} size={"xs"} paddingBottom={true} paddingTop={true} />
          <Input
            label={t("core:position")}
            type="text"
            {...register("position", {
              value: "",
            })}
          />
          <Input
            label={t("common:location")}
            type="text"
            {...register("location", {
              value: "",
            })}
          />
          <Input
            label={t("common:company")}
            type="text"
            {...register("company", {
              value: "",
            })}
          />
          <Controller
            render={({ field: { value, onChange } }) => (
              <Multiselect
                label={t("ats:tags")}
                emptyTitle={t("common:select")}
                isLoading={labelsIsLoading}
                values={value}
                searchable
                onChange={onChange}
                options={labels}
                error={errors.label_ids?.message}
              />
            )}
            control={control}
            name={"label_ids"}
          />
          <Controller
            render={({ field: { value, onChange } }) => (
              <Multiselect
                label={t("common:source")}
                emptyTitle={t("common:select")}
                values={value}
                searchable
                onChange={onChange}
                options={sources}
                error={errors.source_ids?.message}
              />
            )}
            control={control}
            name={"source_ids"}
          />
          <Box className={"py-2"}>
            <Controller
              render={({ field: { value, onChange } }) => (
                <Switch
                  checked={value && employeeMe && value.includes(employeeMe.id)}
                  onChange={(newValue) => {
                    if (newValue) {
                      if (employeeMe) onChange([employeeMe.id]);
                    } else {
                      onChange([]);
                    }
                  }}
                  title={t("ats:my_candidates")}
                  description={t("ats:my_candidates_description")}
                />
              )}
              control={control}
              name={"assigned_by_ids"}
            />
          </Box>
          {canShowJobAndStageFilters && (
            <>
              <Title header={t("ats:job")} size={"xs"} paddingBottom={true} paddingTop={true} />
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Multiselect
                    label={t("ats:job")}
                    emptyTitle={t("common:select")}
                    isLoading={jobsIsLoading}
                    values={value}
                    searchable
                    onSearchOptions={handleSearchJobs}
                    loadOptionsOnOpen
                    searchOptions={(jobsSearched?.items || []) as unknown as SelectItem[]}
                    onChange={(value) => {
                      onChange(value);
                    }}
                    error={errors.job_ids?.message}
                  />
                )}
                control={control}
                name={"job_ids"}
              />
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Multiselect
                    label={t("ats:stages")}
                    emptyTitle={t("common:select")}
                    isLoading={isLoadingStages}
                    values={value}
                    options={currentJob?.pipeline.stages ?? stages}
                    onChange={(value) => {
                      onChange(value);
                    }}
                  />
                )}
                control={control}
                name={"stage_ids"}
              />
            </>
          )}

          <Controller
            control={control}
            name={"date_last_candidate_action_from"}
            render={({ field: fromField }) => (
              <Controller
                control={control}
                name={"date_last_candidate_action_to"}
                render={({ field: toField }) => (
                  <DateRangeFilter
                    title={t("ats:last_action_period")}
                    from={fromField.value ?? undefined}
                    to={toField.value ?? undefined}
                    onChange={({ from, to }) => {
                      fromField.onChange(from ?? null);
                      toField.onChange(to ?? null);
                    }}
                  />
                )}
              />
            )}
          />
          <Title header={t("common:persons")} size={"xs"} paddingBottom={true} paddingTop={true} />
          <Controller
            render={({ field: { value, onChange } }) => (
              <Search
                className={"w-full"}
                placeholder={t("common:words_in_comments") + "..."}
                value={value}
                onInput={onChange}
              />
            )}
            control={control}
            name={"comment"}
          />
          <Controller
            render={({ field: { value, onChange } }) => (
              <Multiselect
                label={t("ats:recruiters_involved")}
                emptyTitle={t("common:select")}
                isLoading={recruitersIsLoading}
                values={value}
                options={recruiters}
                onChange={(value) => {
                  onChange(value);
                }}
              />
            )}
            control={control}
            name={"recruiter_ids"}
          />
          <Controller
            render={({ field: { value, onChange } }) => (
              <Multiselect
                label={t("ats:stakeholders_involved")}
                emptyTitle={t("common:select")}
                isLoading={stakeholdersIsLoading}
                values={value}
                options={stakeholders}
                onChange={(value) => {
                  onChange(value);
                }}
              />
            )}
            control={control}
            name={"stakeholder_ids"}
          />
          <Controller
            render={({ field: { value, onChange } }) => (
              <Multiselect
                label={t("core:people_with_activity")}
                emptyTitle={t("common:select")}
                isLoading={actionEmployeeSearch.isLoading}
                values={value}
                options={formOptions.actionEmployeeSearch}
                searchable
                loadOptionsOnOpen
                onSearchOptions={actionEmployeeSearch.search}
                searchOptions={formOptions.actionEmployeeSearch}
                onChange={(value) => {
                  onChange(value);
                }}
              />
            )}
            control={control}
            name={"action_employee_ids"}
          />
          <Controller
            render={({ field: { value, onChange } }) => (
              <Multiselect
                label={t("ats:authors")}
                emptyTitle={t("common:select")}
                isLoading={authorSearch.isLoading}
                values={value}
                options={formOptions.authorSearch}
                searchable
                loadOptionsOnOpen
                onSearchOptions={authorSearch.search}
                searchOptions={formOptions.authorSearch}
                onChange={(value) => {
                  onChange(value);
                }}
              />
            )}
            control={control}
            name={"author_ids"}
          />
          <Title
            header={t("ats:disqualified")}
            size={"xs"}
            paddingBottom={true}
            paddingTop={true}
          />
          <Controller
            render={({ field: { value, onChange } }) => (
              <Multiselect
                label={t("ats:disqualification_reason")}
                emptyTitle={t("common:select")}
                values={value}
                searchable
                onChange={onChange}
                options={disqualificationReasons}
                error={errors.disqualification_reason_ids?.message}
              />
            )}
            control={control}
            name={"disqualification_reason_ids"}
          />
        </form>
      </Modal>
    </>
  );
}
